/**
 * A simple object pool allocator
 * @module
 */

export class Pool<T> {
  items: T[];

  private blockSize: number;
  private freelist: Int32Array;
  private last: number;
  private initFunc: (index: number) => T;

  constructor(initFunc: (index: number) => T, blockSize: number = 64) {
    this.blockSize = blockSize;
    this.freelist = new Int32Array(blockSize);
    this.freelist.forEach((_, i, a) => (a[i] = i));
    this.last = this.freelist.length - 1;
    this.items = new Array<T>(blockSize);
    for (let i = 0; i < blockSize; i++) {
      this.items[i] = initFunc(i);
    }
    this.initFunc = initFunc;
  }

  alloc() {
    const id = this.allocId();

    return this.items[id];
  }

  allocId() {
    if (this.last > 0) {
      return this.freelist[this.last--];
    }

    // compute expanded pool size
    const oldCount = this.items.length;
    const newCount = (this.items.length + this.blockSize) | 0;
    const addedCount = newCount - oldCount;

    // update free list
    this.freelist = new Int32Array(newCount);
    for (let i = 0; i < addedCount; i++) {
      this.freelist[i] = oldCount + i;
    }
    this.last = addedCount - 1;

    // copy old items to new pool
    const oldItems = this.items;
    const newItems = new Array<T>(newCount);
    for (let i = 0; i < oldCount; i++) {
      newItems[i] = oldItems[i];
    }
    for (let i = oldCount; i < newCount; i++) {
      newItems[i] = this.initFunc(i);
    }
    this.items = newItems;

    return this.freelist[this.last--];
  }

  free(i: number) {
    this.freelist[this.last++] = i;
  }

  reset() {
    this.freelist.forEach((_, i, a) => (a[i] = i));
    this.last = this.freelist.length - 1;
  }
}

export interface PoolSource {
  readonly id: number;
}
